// A machine's workbench (RFC-057): /v1/shares/{id}/workbench, JSON-RPC 2.0
// one object a message, to the machine's sidecar through the relay — its
// files (fs/*, workbench/*) and its agent, byj acp, spoken to in ACP.
//
// WorkbenchConnection keeps the socket: it reconnects with backoff as the
// share's own socket does, and stops for good when the relay says the
// workbench was opened elsewhere (4409), is not the person's to use (4403)
// or is not there (4404). A request is a promise; the agent's own requests
// (`a:<id>`) and every notification go to the page's handlers.
//
// The rest is what the page shows: an ACP thread made from session/update
// notifications, and the languages files are edited in.
import { openLive, type ExamSitting } from "./api";
import { backoffDelay, type SocketLike } from "./live";

export interface RpcMessage {
  jsonrpc: "2.0";
  id?: number | string | null;
  method?: string;
  params?: unknown;
  result?: unknown;
  error?: { code: number; message: string; data?: unknown };
  /** the workbench's own: the agent an ACP message is for, or from (RFC-057 D5) */
  agent?: string;
}

/** A JSON-RPC error, with the workbench's code (Go `workbench.Code*`). */
export class RpcError extends Error {
  constructor(
    readonly code: number,
    message: string,
    readonly data?: unknown,
  ) {
    super(message);
  }
}

/** The workbench's error codes (RFC-057 "The files"). */
export const Codes = {
  notFound: -32001,
  outside: -32002,
  blocked: -32003,
  tooLarge: -32004,
  binary: -32005,
  conflict: -32006,
  exists: -32007,
  notDir: -32008,
  unavailable: -32009,
  closed: -1,
} as const;

/** Close codes after which the page does not reconnect. */
export const FinalCloseCodes: Record<number, WorkbenchEnd> = { 4409: "taken", 4403: "forbidden", 4404: "gone", 4410: "gone" };

export type WorkbenchEnd = "taken" | "forbidden" | "gone" | "left";

export type WorkbenchState =
  | { kind: "connecting" }
  | { kind: "open" }
  | { kind: "waiting"; retryInMs: number }
  | { kind: "closed"; end: WorkbenchEnd };

export interface WorkbenchOptions {
  url: string;
  onNotification: (method: string, params: unknown, agent?: string) => void;
  /** The agent asks the page (session/request_permission): answer with `respond`. */
  onRequest: (id: string, method: string, params: unknown, agent?: string) => void;
  onState?: (s: WorkbenchState) => void;
  /** Opens the socket; by default with a ticket from the main server (openLive). */
  socket?: (url: string) => SocketLike | Promise<SocketLike>;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (id: unknown) => void;
}

interface Pending {
  resolve: (v: unknown) => void;
  reject: (e: RpcError) => void;
}

export class WorkbenchConnection {
  private ws: SocketLike | null = null;
  private next = 0;
  private pending = new Map<number, Pending>();
  private attempt = 0;
  private timer: unknown = null;
  private stopped = false;
  state: WorkbenchState = { kind: "connecting" };

  constructor(private readonly o: WorkbenchOptions) {}

  start(): void {
    this.stopped = false;
    this.connect();
  }

  stop(end: WorkbenchEnd = "left"): void {
    this.stopped = true;
    if (this.timer !== null) (this.o.clearTimer ?? clearTimeout)(this.timer as ReturnType<typeof setTimeout>);
    this.timer = null;
    const ws = this.ws;
    this.ws = null;
    ws?.close(1000, end);
    this.failAll("the workbench is closed");
    this.set({ kind: "closed", end });
  }

  private set(s: WorkbenchState) {
    this.state = s;
    this.o.onState?.(s);
  }

  private connect() {
    this.set({ kind: "connecting" });
    const made = (this.o.socket ?? ((u: string) => openLive(u) as unknown as Promise<SocketLike>))(this.o.url);
    if (made instanceof Promise) {
      // no ticket is a connection lost: tried again, as a socket refused is
      made.then(
        (ws) => this.attach(ws),
        () => {
          if (!this.stopped) this.retry();
        },
      );
      return;
    }
    this.attach(made);
  }

  private retry() {
    const ms = backoffDelay(this.attempt++);
    this.set({ kind: "waiting", retryInMs: ms });
    this.timer = (this.o.setTimer ?? setTimeout)(() => {
      this.timer = null;
      if (!this.stopped) this.connect();
    }, ms);
  }

  private attach(ws: SocketLike) {
    if (this.stopped) {
      ws.close(1000);
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      if (this.ws !== ws) return;
      this.attempt = 0;
      this.set({ kind: "open" });
    };
    ws.onmessage = (ev) => {
      if (this.ws !== ws || typeof ev.data !== "string") return;
      let m: RpcMessage;
      try {
        m = JSON.parse(ev.data) as RpcMessage;
      } catch {
        return;
      }
      this.receive(m);
    };
    ws.onerror = () => {};
    ws.onclose = (ev) => {
      if (this.ws !== ws) return;
      this.ws = null;
      this.failAll("the connection to the machine was lost");
      const end = FinalCloseCodes[ev.code];
      if (end || this.stopped) {
        this.stopped = true;
        this.set({ kind: "closed", end: end ?? "left" });
        return;
      }
      this.retry();
    };
  }

  private failAll(why: string) {
    const p = [...this.pending.values()];
    this.pending.clear();
    for (const x of p) x.reject(new RpcError(Codes.closed, why));
  }

  private receive(m: RpcMessage) {
    // the relay's refusal, said before its close in case the close's code is lost (provenance P-46)
    if (m.method === "workbench/refused") {
      const code = (m.params as { code?: number } | undefined)?.code ?? 4403;
      this.stop(FinalCloseCodes[code] ?? "forbidden");
      return;
    }
    // and its word that a newer page has it: not taken back by reconnecting
    if (m.method === "workbench/taken") {
      this.stop("taken");
      return;
    }
    if (m.method === undefined) {
      if (typeof m.id !== "number") return;
      const p = this.pending.get(m.id);
      if (!p) return;
      this.pending.delete(m.id);
      if (m.error) p.reject(new RpcError(m.error.code, m.error.message, m.error.data));
      else p.resolve(m.result);
      return;
    }
    if (m.id !== undefined && m.id !== null) {
      this.o.onRequest(String(m.id), m.method, m.params, m.agent);
      return;
    }
    this.o.onNotification(m.method, m.params, m.agent);
  }

  private write(m: RpcMessage): boolean {
    if (!this.ws || this.state.kind !== "open") return false;
    this.ws.send(JSON.stringify(m));
    return true;
  }

  /** A request, answered with its result or an RpcError; an ACP one to `agent` (byj when none). */
  request<T = unknown>(method: string, params?: unknown, agent?: string): Promise<T> {
    const id = ++this.next;
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (v: unknown) => void, reject });
      if (!this.write({ jsonrpc: "2.0", id, method, params, ...(agent ? { agent } : {}) })) {
        this.pending.delete(id);
        reject(new RpcError(Codes.closed, "not connected to the machine"));
      }
    });
  }

  notify(method: string, params?: unknown, agent?: string): boolean {
    return this.write({ jsonrpc: "2.0", method, params, ...(agent ? { agent } : {}) });
  }

  /** The answer to the agent's request `id`. */
  respond(id: string, result: unknown): boolean {
    return this.write({ jsonrpc: "2.0", id, result });
  }
}

/** One thing in a folder (Go `workbench.Entry`). */
export interface Entry {
  name: string;
  path: string;
  kind: "file" | "dir" | "link";
  size?: number;
}

export interface FileText {
  path: string;
  text: string;
  version: string;
  size: number;
}

export interface AgentInfo {
  name: string;
  state: "idle" | "running" | "stopped";
  error?: string;
}

export interface WorkbenchInfo {
  root: string;
  cwd: string;
  max_text: number;
  agent?: AgentInfo;
  /** every agent the workbench has, byj first (RFC-057 D5) */
  agents?: AgentInfo[];
  /** the assignment the machine is sat for: the page is its exam room (RFC-058) */
  exam?: string;
}

/** A question of the paper as the exam room shows it (`byj exam status --json`). */
export interface ExamQuestion {
  id: string;
  mode: string;
  prompt?: string;
  submit?: string[];
  "handed-in": number;
  /** a multiple-choice question's options, how many to choose and the answer kept */
  options?: string[];
  choose?: number;
  answer?: number[] | null;
  /** a practical question's folder in the attempt */
  workspace?: string;
}

/** The sitting as the exam room shows it (RFC-058): times in ms, the deadline on the relay's clock. */
export interface ExamState {
  assignment: string;
  prepared: boolean;
  over?: boolean;
  /** finished before its deadline, by Hand in all and finish (byj-1wv.113): over at once */
  finished?: boolean;
  begun?: boolean;
  exam?: string;
  title?: string;
  /** the agents the paper allows, and the one the attempt is sat with */
  agents?: string[];
  agent?: string;
  model?: string;
  "started-at"?: number | null;
  deadline?: number | null;
  /** how far the relay's clock is ahead of the machine's */
  "clock-offset-ms"?: number;
  /** the machine's clock as it answered */
  now?: number;
  "time-limit-min"?: number | null;
  attempts?: number;
  /** what the end would hand in */
  pending?: string[];
  questions?: ExamQuestion[];
  /**
   * Not the machine's: set by the page from the relay's sitting
   * (examWithSitting) when the relay ends it before the deadline the
   * machine read at Begin — the candidate's own end or the window's close
   * moved since, as an operator's end now does (byj-1wv.118.19).
   */
  endedEarly?: boolean;
  /**
   * Not the machine's either: the deadline the machine read at Begin, kept
   * by examWithSitting when the relay's sooner end replaces `deadline` —
   * the sitting as it was set, which its warnings go by (byj-1wv.118.20).
   */
  scheduled?: number | null;
}

/** How far the relay's clock is ahead of this browser's, from a state read at `at`. */
export function examSkew(st: ExamState, at = Date.now()): number {
  return st.now === undefined ? 0 : st.now + (st["clock-offset-ms"] ?? 0) - at;
}

/** The ms left of a sitting at the browser's `now`, on the relay's clock; null with no deadline. */
export function examLeft(st: ExamState, skew: number, now = Date.now()): number | null {
  return st.deadline == null ? null : Math.max(0, st.deadline - (now + skew));
}

/**
 * The sitting as the room counts it: the machine's, with the relay's sitting
 * (`GET /v1/exam-assignments/{id}`) folded in. The machine keeps the
 * deadline it read at Begin, while the relay's takes the candidate's own end
 * and the window's close as they are now (RFC-058 "The relay keeps the
 * time"): the deadline is the earlier of the two, both on the relay's
 * clock; the sitting is over once the relay closes it, past its grace; and
 * an end the relay sets before the machine's is the room's `endedEarly`
 * (byj-1wv.118.19). No sitting, or none begun: the machine's as it is.
 */
export function examWithSitting(st: ExamState, sitting?: ExamSitting | null): ExamState {
  if (!sitting || !st.begun) return st;
  const relay = sitting.deadline ? Date.parse(sitting.deadline) : NaN;
  if (Number.isNaN(relay)) return sitting.closed && !st.over ? { ...st, over: true } : st;
  // the same deadline, rounded apart by the two, is not an end moved; nor is
  // the candidate's own finish, which the relay may hold before the machine
  // says it. An end moved sooner stays one once the machine reaches its own
  // later deadline and says it is over, having handed in what was left a
  // minute before it: whether that was taken is the relay's to say, so the
  // room keeps promising nothing
  // (byj-1wv.118.20)
  const early = !st.finished && !sitting.finished_at && (st.deadline == null || relay < st.deadline - 1000);
  return {
    ...st,
    deadline: early ? relay : st.deadline,
    over: st.over === true || sitting.closed,
    ...(early ? { endedEarly: true, scheduled: st.deadline } : {}),
  };
}

/** A countdown as a person reads it: 1:05:09, 42:10, 0:59. */
export function clockText(ms: number): string {
  const s = Math.ceil(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${sec}` : `${m}:${sec}`;
}

/**
 * The warning a countdown has reached: 15, 5 or 1 min left, or none. A
 * warning is only for a sitting longer than it: a 12-minute paper is not
 * told it has less than 15 minutes from its first second (byj-1wv.118.20).
 * `length` is the sitting's, its deadline less its start; unknown, any.
 */
export function examWarning(left: number | null, length?: number | null): 15 | 5 | 1 | null {
  if (left === null || left <= 0) return null;
  for (const min of [1, 5, 15] as const) {
    // longer by more than the second two clocks may round a time apart by
    if (left <= min * 60_000) return length == null || length > min * 60_000 + 1000 ? min : null;
  }
  return null;
}

/**
 * Whether the room's bar and notice read the sitting as over: the machine
 * says so, or no time is left. The prompt's lock and a question's hand-in
 * go by their own (a question is still handed in within the grace).
 */
export function examOver(st: ExamState, left: number | null): boolean {
  return st.over === true || left === 0;
}

/** The warning the room's bar and notice both show for `st` with `left` ms to go: none once it is over. */
export function examWarningOf(st: ExamState, left: number | null): 15 | 5 | 1 | null {
  return examOver(st, left) ? null : examWarning(left, examLength(st));
}

/**
 * A sitting's length in ms as it was set at Begin: the deadline the machine
 * read then less its start, not an end the relay moved sooner since, which
 * is when a warning matters most; null when either is not known.
 */
export function examLength(st: ExamState): number | null {
  const started = st["started-at"];
  // a machine with no deadline of its own: the relay's is the one there is
  const end = st.scheduled ?? st.deadline;
  return started != null && end != null ? end - started : null;
}

/** A question's last hand-in as the machine told it (`exam/submit`'s receipt), and when, by this browser's clock. */
export interface HandIn {
  text: string;
  at: number;
}

/** A hand-in as the room shows it, read from what `byj exam submit` printed (exam_submit.clj receipt-text). */
export interface Receipt {
  question: string;
  /** which hand-in of the question this was, from 1 */
  n: number;
  late: boolean;
  /** a practical question's files, as handed in */
  files?: string[];
  /** a choice question's answers, numbered from 1 */
  answered?: number[];
  missing?: string[];
  /** files named to be handed in that are not text, left out */
  binary?: string[];
  sessions?: number;
  toolCalls?: number;
  /** the start of the bundle's hash: what the student quotes to an instructor */
  hash?: string;
}

const listOf = (s: string) =>
  s
    .split(",")
    .map((x) => x.trim())
    .filter((x) => x !== "" && x !== "none");

/**
 * The hand-ins in what `byj exam submit` printed (byj-1wv.51.23), one a
 * question — `handed in Q (N)` and its indented lines — so the room shows
 * them in its own words rather than the CLI's; null when nothing in it reads
 * as a hand-in, and the room shows it as it was written. A line it does not
 * know is passed over: what byj adds later costs nothing here.
 */
export function parseReceipt(text: string): Receipt[] | null {
  const out: Receipt[] = [];
  for (const line of text.split("\n")) {
    const head = /^handed in (\S+) \((\d+)\)(.*)$/.exec(line);
    if (head) {
      out.push({ question: head[1]!, n: Number(head[2]), late: head[3]!.includes("LATE") });
      continue;
    }
    const r = out[out.length - 1];
    const field = /^\s+(files|answered|missing|binary, not kept|session|bundle)\s+(.*)$/.exec(line);
    if (!r || !field) continue;
    const [, key, value] = field as unknown as [string, string, string];
    switch (key) {
      case "files":
        r.files = listOf(value);
        break;
      case "answered":
        r.answered = listOf(value)
          .map(Number)
          .filter((x) => Number.isFinite(x));
        break;
      case "missing":
        r.missing = listOf(value);
        break;
      case "binary, not kept":
        r.binary = listOf(value);
        break;
      case "session": {
        const m = /(\d+) session\(s\), (\d+) tool call\(s\)/.exec(value);
        if (m) {
          r.sessions = Number(m[1]);
          r.toolCalls = Number(m[2]);
        }
        break;
      }
      case "bundle":
        r.hash = value.trim().split(/\s+/).pop();
        break;
    }
  }
  return out.length > 0 ? out : null;
}

/** An agent as a person reads its name. */
export function agentLabel(name: string): string {
  switch (name) {
    case "claude":
      return "Claude Code";
    case "codex":
      return "Codex";
  }
  return name;
}

/** A thread to go back to (RFC-057 "Going back to a thread"): open in the workbench's agent, saved, or open elsewhere — a terminal's byj — and not to be opened here. */
export interface PastThread {
  sessionId: string;
  title: string;
  updatedAt?: string;
  turns?: number;
  state: "open" | "saved" | "elsewhere";
  busy?: boolean;
}

/** A folder worth a thread of its own: an exam attempt's question (Go `workbench.Project`). */
export interface Project {
  path: string;
  exam: string;
  question: string;
  /** the agent the attempt is sat with, which a thread on it must be */
  agent?: string;
  /** the paper's model, when it names one */
  model?: string;
}

// The thread: what the agent and the person said, as ACP's session/update
// carries it (RFC-053, byj acp's on-event).

export interface ToolItem {
  kind: "tool";
  id: string;
  title: string;
  toolKind: string;
  status: "pending" | "in_progress" | "completed" | "failed";
  input?: unknown;
  output?: string;
}

export interface PermissionOption {
  optionId: string;
  name?: string;
  kind?: string;
}

export interface PermissionItem {
  kind: "permission";
  /** the session it is asked in */
  sessionId?: string;
  /** The agent's request, as the page answers it (`a:<id>`). */
  rpc: string;
  toolCallId: string;
  title: string;
  options: PermissionOption[];
  chosen?: string;
}

export type ThreadItem =
  | { kind: "user"; text: string }
  | { kind: "agent"; text: string }
  | { kind: "thought"; text: string }
  | { kind: "error"; text: string }
  | ToolItem
  | PermissionItem;

export interface Thread {
  sessionId?: string;
  items: ThreadItem[];
  /** A prompt is being answered. */
  busy: boolean;
}

export const emptyThread: Thread = { items: [], busy: false };

export type ThreadAction =
  | { type: "session"; sessionId: string }
  | { type: "prompt"; text: string }
  | { type: "update"; params: unknown }
  | { type: "permission"; rpc: string; params: unknown }
  | { type: "chosen"; rpc: string; optionId: string }
  | { type: "done"; stopReason?: string }
  | { type: "failed"; message: string }
  | { type: "reset" }
  /** another session shown: what is said of it is kept, and what it asks that is not answered yet */
  | { type: "switch"; sessionId: string }
  | { type: "busy"; busy: boolean };

interface UpdateParams {
  sessionId?: string;
  update?: {
    sessionUpdate?: string;
    content?: { type?: string; text?: string } | { type?: string; content?: { text?: string } }[];
    toolCallId?: string;
    title?: string;
    kind?: string;
    status?: ToolItem["status"];
    rawInput?: unknown;
  };
}

function textOf(c: unknown): string {
  if (c && typeof c === "object" && !Array.isArray(c)) return (c as { text?: string }).text ?? "";
  if (Array.isArray(c)) return c.map((b: { content?: { text?: string } }) => b.content?.text ?? "").join("\n");
  return "";
}

/** Text streamed in chunks joins the last item of its kind, if it is the last item. */
function appendText(items: ThreadItem[], kind: "user" | "agent" | "thought", text: string): ThreadItem[] {
  const last = items[items.length - 1];
  if (last && last.kind === kind) return [...items.slice(0, -1), { kind, text: last.text + text }];
  return [...items, { kind, text }];
}

export function reduceThread(t: Thread, a: ThreadAction): Thread {
  switch (a.type) {
    case "reset":
      return emptyThread;
    case "switch":
      return { sessionId: a.sessionId, busy: false, items: t.items.filter((x) => x.kind === "permission" && x.chosen === undefined && x.sessionId === a.sessionId) };
    case "busy":
      return { ...t, busy: a.busy };
    case "session":
      return { ...t, sessionId: a.sessionId };
    case "prompt":
      return { ...t, busy: true, items: [...t.items, { kind: "user", text: a.text }] };
    case "done":
      return { ...t, busy: false };
    case "failed":
      return { ...t, busy: false, items: [...t.items, { kind: "error", text: a.message }] };
    case "chosen":
      return { ...t, items: t.items.map((x) => (x.kind === "permission" && x.rpc === a.rpc ? { ...x, chosen: a.optionId } : x)) };
    case "permission": {
      const p = a.params as { sessionId?: string; toolCall?: { toolCallId?: string; title?: string }; options?: PermissionOption[] };
      if (t.items.some((x) => x.kind === "permission" && x.rpc === a.rpc)) return t;
      return {
        ...t,
        items: [
          ...t.items,
          { kind: "permission", rpc: a.rpc, sessionId: p.sessionId, toolCallId: p.toolCall?.toolCallId ?? "", title: p.toolCall?.title ?? "", options: p.options ?? [] },
        ],
      };
    }
    case "update": {
      const p = a.params as UpdateParams;
      if (t.sessionId && p.sessionId && p.sessionId !== t.sessionId) return t;
      const u = p.update;
      if (!u) return t;
      switch (u.sessionUpdate) {
        case "user_message_chunk":
          return { ...t, items: appendText(t.items, "user", textOf(u.content)) };
        case "agent_message_chunk":
          return { ...t, items: appendText(t.items, "agent", textOf(u.content)) };
        case "agent_thought_chunk":
          return { ...t, items: appendText(t.items, "thought", textOf(u.content)) };
        case "tool_call":
          return {
            ...t,
            items: [...t.items, { kind: "tool", id: u.toolCallId ?? "", title: u.title ?? "", toolKind: u.kind ?? "other", status: u.status ?? "pending", input: u.rawInput }],
          };
        case "tool_call_update":
          return {
            ...t,
            items: t.items.map((x) =>
              x.kind === "tool" && x.id === u.toolCallId
                ? { ...x, status: u.status ?? x.status, output: u.content !== undefined ? textOf(u.content) : x.output }
                : x,
            ),
          };
      }
      return t;
    }
  }
}

/** The permission requests not answered yet. */
export function openPermissions(t: Thread): PermissionItem[] {
  return t.items.filter((x): x is PermissionItem => x.kind === "permission" && x.chosen === undefined);
}

/** The language a file is edited in, by its name; undefined for plain text. */
export function languageOf(path: string): "python" | "javascript" | "typescript" | "json" | "markdown" | "html" | "css" | undefined {
  const name = path.split("/").pop()!.toLowerCase();
  const ext = name.includes(".") ? name.split(".").pop()! : "";
  switch (ext) {
    case "py":
      return "python";
    case "js":
    case "mjs":
    case "cjs":
    case "jsx":
      return "javascript";
    case "ts":
    case "tsx":
    case "mts":
      return "typescript";
    case "json":
      return "json";
    case "md":
    case "markdown":
      return "markdown";
    case "html":
    case "htm":
      return "html";
    case "css":
      return "css";
  }
  return undefined;
}

/** A file open in the editor: what it holds now, and the version it was read or saved at. */
export interface OpenFile {
  path: string;
  text: string;
  saved: string;
  version: string;
  /** The machine has it otherwise than the editor read it, and the editor has changes too. */
  conflict?: boolean;
  /** It is not on the machine any more. */
  gone?: boolean;
}

export function dirty(f: OpenFile): boolean {
  return f.text !== f.saved;
}
